import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, AlignHorizontalDistributeCenter, AudioLines, Blend, ChevronDown, ChevronLeft, ChevronRight, Download, Film, Image as ImageIcon, ImagePlus, Images, Info, Loader2, Maximize2, MessageSquare, Mic, Minus, MoveRight,
  Music, Plus, Redo2, RefreshCw, Scissors, SlidersHorizontal, Sparkles, Undo2, Volume2, VolumeX, X,
} from 'lucide-react';
import {
  audioVersions,
  BOARD_ASPECTS,
  clampCommentBox,
  COMMENT_TEXT_MAX,
  chainForVideo,
  edgeAnchors,
  clampNodeScale,
  imageVersions,
  nodeFrame,
  nodeScale,
  nodeSize,
  scriptDurationTitle,
  scriptOwnedSeconds,
  videoVersions,
  type Board,
  type BoardAspect,
  type BoardAudioNode,
  type BoardCommentNode,
  type BoardGenerator,
  type BoardImageMode,
  type BoardImageNode,
  type BoardJoinTransition,
  type BoardMediaRef,
  type BoardNode,
  type BoardVideoNode,
  type GeneratorSlot,
} from '../../lib/canvasBoard';
import {
  alwaysGeneratedAudio,
  defaultGenerateAudio,
  getVideoModelById,
  listModelsForModality,
  supportsGeneratedAudio,
} from '../../lib/videoModelCatalog';
import type { SceneImageRole } from '../../lib/canvasPipeline';
import { downloadMedia, friendlyFileName } from '../../lib/downloadMedia';
import { GALLERY_DRAG_TYPE, type GalleryDragItem } from './CanvasGalleryTray';
import PixelAssemble from './PixelAssemble';
import FrameCrossfade, { generatorPreviewUrls } from './FrameCrossfade';
import CanvasNodeInfo from './CanvasNodeInfo';
import CanvasContextMenu, { actionForKey, type CanvasAction } from './CanvasContextMenu';

const INFO_W = 320;

export type BoardCanvasHandle = {
  /** World point at the middle of the visible area. */
  viewCenter: () => { x: number; y: number };
  fitView: () => void;
  /** Shift the camera by screen pixels (e.g. keep content centered when a side panel opens). */
  panBy: (dx: number, dy: number) => void;
};

/** `label`: first scene of the flow, used to name downloads. `refs`: the flow's images in order. */
export type VideoMeta = {
  scenes: number;
  seconds: number;
  joins: number;
  tracks?: number;
  label?: string;
  refs?: Array<{ id: string; url?: string; label: string }>;
  /** Full prompt of the current script (planned flows), shown in the generator card. */
  prompt?: string;
};

export type AudioTrackPatch = Partial<Pick<BoardAudioNode, 'volume' | 'startOffset' | 'loop' | 'muted' | 'speed'>>;

export type NodeMove = { id: string; x: number; y: number };

type Props = {
  board: Board;
  selectedIds: string[];
  sceneIndex: Map<string, number>;
  /** How each scene image drives its flow's video (start/end frame or style reference). */
  sceneRole?: Map<string, SceneImageRole>;
  videoMeta: Map<string, VideoMeta>;
  projectTitle?: string;
  onSelect: (ids: string[]) => void;
  onMoveNodes: (moves: NodeMove[]) => void;
  onResizeNode?: (id: string, scale: number) => void;
  onConnect: (from: string, to: string) => void;
  /** Several selected images dragged as one link: onto a node (`to`) or empty canvas (`to` null → new video at `at`). */
  onConnectImages?: (imageIds: string[], to: string | null, at: { x: number; y: number }, order?: 'reading' | 'given') => void;
  onDeleteEdge: (edgeId: string) => void;
  onRemoveNodes: (ids: string[]) => void;
  /** Ctrl/Cmd+C of the current selection (nodes and arrows between them). */
  onCopySelection?: (selectedIds: string[], selectedEdgeId: string | null) => void;
  /** Ctrl/Cmd+V of a board clipboard snapshot near `at`. */
  onPasteClipboard?: (at: { x: number; y: number }) => void;
  /** Image, video and audio files dropped or picked from the computer. */
  onDropFiles: (files: File[], at: { x: number; y: number }) => void;
  onDropUrls: (urls: string[], at: { x: number; y: number }) => void;
  onDropGallery: (items: GalleryDragItem[], at: { x: number; y: number }) => void;
  onOpenGallery: () => void;
  onSelectVersion: (videoId: string, versionId: string) => void;
  onSelectAudioVersion: (audioId: string, versionId: string) => void;
  onSelectImageVersion?: (imageId: string, versionId: string) => void;
  /** Double-click on an image/video node: full-screen view with its history. */
  onOpenMedia?: (nodeId: string) => void;
  onUpdateAudio: (audioId: string, patch: AudioTrackPatch) => void;
  onRegenerateAudio: (audioId: string) => void;
  onSetImageMode?: (videoId: string, mode: BoardImageMode) => void;
  /** Generator card: model, prompt, start/end frames, attachments, reference video, audio. */
  onPatchVideo?: (videoId: string, patch: Partial<BoardVideoNode>) => void;
  onUploadGenerator?: (videoId: string, slot: GeneratorSlot, file: File) => void;
  onGenerateVideo?: (videoId: string) => void;
  /** Nodes showing the assemble animation before their status flips to generating. */
  assemblingIds?: string[];
  aspect?: BoardAspect;
  onAspect?: (aspect: BoardAspect) => void;
  /** Right-click menu / Shift shortcuts, except uploads (handled here). */
  onAction?: (action: CanvasAction, at: { x: number; y: number }) => void;
  /** Shared "can view" projects: select, play and read the script, but no edits. */
  readOnly?: boolean;
  /** Board history: Ctrl/Cmd+Z, Ctrl+Y / Ctrl+Shift+Z and the zoom-bar buttons. */
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** Line up the flows left-to-right and stack separate ones. */
  onLayout?: () => void;
  /** Text or size of a comment box. */
  onUpdateComment?: (id: string, patch: { text?: string; w?: number; h?: number }) => void;
  /** The comment just placed; its box focuses once. */
  focusCommentId?: string | null;
  onCommentFocused?: () => void;
};

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2.5;
const clampZoom = (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));

const UPLOAD_ACCEPT: Partial<Record<CanvasAction, string>> = {
  'upload-image': 'image/*',
  'upload-video': 'video/*',
  'upload-audio': 'audio/*',
};

type Drag =
  | { kind: 'pan'; startX: number; startY: number; panX: number; panY: number }
  | { kind: 'node'; startX: number; startY: number; grabX: number; grabY: number; origin: NodeMove[]; moved: boolean }
  | { kind: 'marquee'; startX: number; startY: number; base: string[]; moved: boolean }
  | { kind: 'link'; from: string; batch?: string[] };

function isMediaFile(f: File): boolean {
  return /^(image|video|audio)\//.test(f.type);
}

function nodeBoxStyle(node: BoardNode, aspect: BoardAspect = '16:9'): React.CSSProperties {
  const frame = nodeFrame(node, aspect);
  const scale = nodeScale(node);
  return {
    left: node.x,
    top: node.y,
    width: frame.w,
    height: frame.h,
    transform: scale === 1 ? undefined : `scale(${scale})`,
    transformOrigin: 'top left',
  };
}

function capturePointer(e: React.PointerEvent) {
  const el = e.currentTarget;
  if (!(el instanceof HTMLElement)) return;
  try { el.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }
}

function isTypingTarget(el: EventTarget | null): boolean {
  const t = el as HTMLElement | null;
  if (!t) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'url', 'email', 'password', 'number', 'tel', '']);

/** Prompt/chat fields keep native undo while the text differs from when they were focused. */
function isTextControl(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type);
  return false;
}

/** Scene→scene arrows carry the source scene's transition; other arrows have none. */
/** Travel of a new link, then the receiver lights up. Keep in step with `.tb-edge-energy` (1.05s). */
const LINK_FLOW_MS = 1050;
const LINK_NODE_MS = 1200;

function sceneTransition(a: BoardNode, b: BoardNode): 'cut' | 'camera' | null {
  if (a.kind !== 'image' || b.kind !== 'image') return null;
  return a.phase.transition_out === 'cut' ? 'cut' : 'camera';
}

function edgeCurve(a: BoardNode, b: BoardNode, aspect: BoardAspect = '16:9') {
  const c = edgeAnchors(a, b, aspect);
  return { x1: c.x1, y1: c.y1, c1x: c.c1x, c2x: c.c2x, x2: c.x2, y2: c.y2 };
}

function edgePath(a: BoardNode, b: BoardNode, aspect: BoardAspect = '16:9', dy = 0): string {
  const c = edgeCurve(a, b, aspect);
  return `M ${c.x1} ${c.y1 + dy} C ${c.c1x} ${c.y1 + dy}, ${c.c2x} ${c.y2 + dy}, ${c.x2} ${c.y2 + dy}`;
}

/** Point at `t` (0 = source, 1 = target) along the edge's curve. */
function edgePoint(a: BoardNode, b: BoardNode, t: number, aspect: BoardAspect = '16:9', dy = 0): { x: number; y: number } {
  const c = edgeCurve(a, b, aspect);
  const y1 = c.y1 + dy;
  const y2 = c.y2 + dy;
  const u = 1 - t;
  return {
    x: u * u * u * c.x1 + 3 * u * u * t * c.c1x + 3 * u * t * t * c.c2x + t * t * t * c.x2,
    y: u * u * u * y1 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y2,
  };
}

/** Lift the dashed reference curve when the same pair also has the video arrow. */
function referenceLane(edges: Array<{ from: string; to: string; role?: string }>, edge: { from: string; to: string; role?: string }): number {
  if (edge.role !== 'ref') return 0;
  const hasFlow = edges.some((e) => e.role !== 'ref' && e.from === edge.from && e.to === edge.to);
  return hasFlow ? -26 : 0;
}

const secsPt = (n: number) => `${(Math.round(n * 10) / 10).toLocaleString('pt-BR')}s`;

function joinTransitionLabel(t: BoardJoinTransition): string {
  return `${t.kind === 'crossfade' ? 'Transição' : 'Fade'} ${secsPt(t.seconds)}`;
}

const BoardCanvas = forwardRef<BoardCanvasHandle, Props>(function BoardCanvas(props, ref) {
  const { board, selectedIds, sceneIndex, videoMeta, onSelect, onMoveNodes, onConnect, onDeleteEdge, onRemoveNodes, onCopySelection, onPasteClipboard, readOnly } = props;
  const viewportRef = useRef<HTMLDivElement>(null);
  const [infoId, setInfoId] = useState<string | null>(null);
  const [pan, setPan] = useState({ x: 80, y: 40 });
  const [zoom, setZoom] = useState(1);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [dragOffset, setDragOffset] = useState<{ ids: Set<string>; dx: number; dy: number } | null>(null);
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [linkTip, setLinkTip] = useState<{ x: number; y: number } | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [freshEdges, setFreshEdges] = useState<Set<string>>(() => new Set());
  const [freshNodes, setFreshNodes] = useState<Set<string>>(() => new Set());
  const seenEdgesRef = useRef<Set<string> | null>(null);
  const flashTimersRef = useRef<number[]>([]);
  /** A drag that actually moved should not also open the video. */
  const skipOpenRef = useRef(false);
  const [dropActive, setDropActive] = useState(false);
  const [spaceDown, setSpaceDown] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; at: { x: number; y: number } } | null>(null);
  const viewRef = useRef({ pan, zoom });
  viewRef.current = { pan, zoom };

  useEffect(() => () => {
    flashTimersRef.current.forEach((id) => window.clearTimeout(id));
  }, []);

  useEffect(() => {
    const ids = new Set(board.edges.map((e) => e.id));
    const prev = seenEdgesRef.current;
    seenEdgesRef.current = ids;
    if (!prev) return;
    const added = board.edges.filter((e) => !prev.has(e.id));
    const kept = board.edges.some((e) => prev.has(e.id));
    if (added.length === 0) return;
    if (!kept && added.length > 1) return;
    const byNode = new Map(board.nodes.map((n) => [n.id, n]));
    const lineEdges = added.filter((edge) => {
      const from = byNode.get(edge.from);
      const to = byNode.get(edge.to);
      return Boolean(from && to && sceneTransition(from, to) !== 'cut');
    });
    const lineNodes = new Set(lineEdges.map((edge) => edge.to));
    const instantNodes = added.map((edge) => edge.to).filter((id) => !lineNodes.has(id));
    const showNodes = (nodeIds: string[]) => {
      if (!nodeIds.length) return;
      setFreshNodes((cur) => {
        const next = new Set(cur);
        nodeIds.forEach((id) => next.add(id));
        return next;
      });
    };
    const hideNodes = (nodeIds: string[]) => {
      setFreshNodes((cur) => {
        const next = new Set(cur);
        nodeIds.forEach((id) => next.delete(id));
        return next;
      });
    };
    const later = (fn: () => void, ms: number) => {
      flashTimersRef.current.push(window.setTimeout(fn, ms));
    };
    if (lineEdges.length) {
      const edgeIds = lineEdges.map((edge) => edge.id);
      const nodeIds: string[] = [];
      lineNodes.forEach((id) => nodeIds.push(id));
      setFreshEdges((cur) => {
        const next = new Set(cur);
        edgeIds.forEach((id) => next.add(id));
        return next;
      });
      later(() => {
        setFreshEdges((cur) => {
          const next = new Set(cur);
          edgeIds.forEach((id) => next.delete(id));
          return next;
        });
        showNodes(nodeIds);
        later(() => hideNodes(nodeIds), LINK_NODE_MS);
      }, LINK_FLOW_MS);
    }
    if (instantNodes.length) {
      showNodes(instantNodes);
      later(() => hideNodes(instantNodes), LINK_NODE_MS);
    }
  }, [board.edges, board.nodes]);

  const nodesRef = useRef(board.nodes);
  nodesRef.current = board.nodes;
  const spaceRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAtRef = useRef({ x: 0, y: 0 });
  const zoomAnimRef = useRef<{ target: number; mx: number; my: number; raf: number } | null>(null);
  const onActionRef = useRef(props.onAction);
  onActionRef.current = props.onAction;
  const onUndoRef = useRef(props.onUndo);
  onUndoRef.current = props.onUndo;
  const onRedoRef = useRef(props.onRedo);
  onRedoRef.current = props.onRedo;
  /** Value of a text field when it received focus; Ctrl+Z edits the board once the field is back to this. */
  const textBaseRef = useRef(new WeakMap<HTMLInputElement | HTMLTextAreaElement, string>());
  const onConnectImagesRef = useRef(props.onConnectImages);
  onConnectImagesRef.current = props.onConnectImages;
  const onMoveNodesRef = useRef(onMoveNodes);
  onMoveNodesRef.current = onMoveNodes;
  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const boardRef = useRef(board);
  boardRef.current = board;

  /** Pan/zoom that later events in the same frame can build on (state only lands on the next render). */
  const setView = useCallback((p: { x: number; y: number }, z: number) => {
    viewRef.current = { pan: p, zoom: z };
    setPan(p);
    setZoom(z);
  }, []);

  const stopZoomAnim = useCallback(() => {
    const a = zoomAnimRef.current;
    if (a) cancelAnimationFrame(a.raf);
    zoomAnimRef.current = null;
  }, []);

  /** Zoom keeping the screen point (mx, my) fixed. */
  const zoomAt = useCallback((mx: number, my: number, z: number) => {
    const { pan: p, zoom: cur } = viewRef.current;
    const next = clampZoom(z);
    setView({ x: mx - ((mx - p.x) / cur) * next, y: my - ((my - p.y) / cur) * next }, next);
  }, [setView]);

  /** Eased zoom toward `factor` × the pending target (mouse wheel, buttons). */
  const smoothZoom = useCallback((mx: number, my: number, factor: number) => {
    const a = zoomAnimRef.current;
    const target = clampZoom((a ? a.target : viewRef.current.zoom) * factor);
    if (a) {
      Object.assign(a, { target, mx, my });
      return;
    }
    const anim = { target, mx, my, raf: 0 };
    zoomAnimRef.current = anim;
    const step = () => {
      const z = viewRef.current.zoom;
      const diff = anim.target - z;
      const next = Math.abs(diff) < 0.002 ? anim.target : z + diff * 0.3;
      zoomAt(anim.mx, anim.my, next);
      if (next === anim.target) {
        zoomAnimRef.current = null;
        return;
      }
      anim.raf = requestAnimationFrame(step);
    };
    anim.raf = requestAnimationFrame(step);
  }, [zoomAt]);

  useEffect(() => stopZoomAnim, [stopZoomAnim]);

  const zoomFromCenter = (factor: number) => {
    const el = viewportRef.current;
    smoothZoom((el?.clientWidth || 0) / 2, (el?.clientHeight || 0) / 2, factor);
  };

  const toWorld = useCallback((clientX: number, clientY: number) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const { pan: p, zoom: z } = viewRef.current;
    return {
      x: (clientX - (rect?.left || 0) - p.x) / z,
      y: (clientY - (rect?.top || 0) - p.y) / z,
    };
  }, []);
  const toWorldRef = useRef(toWorld);
  toWorldRef.current = toWorld;

  const fitView = useCallback(() => {
    const el = viewportRef.current;
    if (!el || !board.nodes.length) {
      stopZoomAnim();
      setView({ x: 80, y: 40 }, 1);
      return;
    }
    const minX = Math.min(...board.nodes.map((n) => n.x));
    const minY = Math.min(...board.nodes.map((n) => n.y));
    const maxX = Math.max(...board.nodes.map((n) => n.x + nodeSize(n, board.aspectRatio).w));
    const maxY = Math.max(...board.nodes.map((n) => n.y + nodeSize(n, board.aspectRatio).h));
    const pad = 80;
    const usableH = el.clientHeight * 0.72;
    const z = Math.max(ZOOM_MIN, Math.min(1.2, Math.min((el.clientWidth - pad * 2) / (maxX - minX || 1), (usableH - pad) / (maxY - minY || 1))));
    stopZoomAnim();
    setView({
      x: (el.clientWidth - (maxX - minX) * z) / 2 - minX * z,
      y: Math.max(24, (usableH - (maxY - minY) * z) / 2) - minY * z,
    }, z);
  }, [board.aspectRatio, board.nodes, setView, stopZoomAnim]);

  const infoNode = infoId ? board.nodes.find((n) => n.id === infoId) : undefined;
  const infoStyle = (node: BoardNode): React.CSSProperties => {
    const el = viewportRef.current;
    const w = el?.clientWidth || 1200;
    const h = el?.clientHeight || 800;
    const size = nodeSize(node, board.aspectRatio);
    const right = pan.x + (node.x + size.w) * zoom + 12;
    const left = right + INFO_W > w - 12 ? Math.max(12, pan.x + node.x * zoom - INFO_W - 12) : right;
    const top = Math.min(Math.max(12, pan.y + node.y * zoom), Math.max(12, h - 240));
    return { left, top, width: INFO_W, maxHeight: Math.max(200, h - top - 24) };
  };

  useImperativeHandle(ref, () => ({
    viewCenter: () => {
      const el = viewportRef.current;
      const rect = el?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return toWorld(rect.left + rect.width / 2, rect.top + rect.height * 0.35);
    },
    fitView,
    panBy: (dx, dy) => {
      stopZoomAnim();
      const { pan: p, zoom: z } = viewRef.current;
      setView({ x: p.x + dx, y: p.y + dy }, z);
    },
  }), [toWorld, fitView, setView, stopZoomAnim]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as HTMLElement)?.closest('video, .tb-info, .tb-ctx')) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;
      const dx = e.deltaX * unit;
      const dy = e.deltaY * unit;
      if (e.ctrlKey || e.metaKey) {
        // Trackpad pinch arrives as ctrl+wheel with small deltas.
        stopZoomAnim();
        zoomAt(mx, my, viewRef.current.zoom * Math.exp(-dy * 0.012));
        return;
      }
      // Two-finger trackpad scroll: pixel deltas, often fractional or sideways. A mouse wheel sends big integer steps.
      const trackpad = e.deltaMode === 0 && (dx !== 0 || !Number.isInteger(dy) || Math.abs(dy) < 40);
      if (trackpad || e.shiftKey) {
        stopZoomAnim();
        const { pan: p, zoom: z } = viewRef.current;
        const sx = e.shiftKey && !dx ? dy : dx;
        const sy = e.shiftKey && !dx ? 0 : dy;
        setView({ x: p.x - sx, y: p.y - sy }, z);
        return;
      }
      smoothZoom(mx, my, Math.exp(-dy * 0.0022));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setView, smoothZoom, stopZoomAnim, zoomAt]);

  useEffect(() => {
    if (!drag) return;
    const nodeUnderPointer = (clientX: number, clientY: number, ignore: ReadonlySet<string>) => {
      const ordered: string[] = [];
      const seen = new Set<string>();
      const push = (id: string | null | undefined) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        ordered.push(id);
      };
      for (const el of document.elementsFromPoint(clientX, clientY)) {
        if (!(el instanceof Element)) continue;
        push(el.closest('[data-node-id]')?.getAttribute('data-node-id'));
      }
      const w = toWorldRef.current(clientX, clientY);
      const list = nodesRef.current;
      for (let i = list.length - 1; i >= 0; i--) {
        const n = list[i];
        const s = nodeSize(n, board.aspectRatio);
        if (w.x >= n.x - 28 && w.x <= n.x + s.w + 12 && w.y >= n.y - 12 && w.y <= n.y + s.h + 12) push(n.id);
      }
      return ordered.find((id) => !ignore.has(id)) || null;
    };
    const onMove = (e: PointerEvent) => {
      if (drag.kind === 'pan') {
        setView({ x: drag.panX + e.clientX - drag.startX, y: drag.panY + e.clientY - drag.startY }, viewRef.current.zoom);
      } else if (drag.kind === 'node') {
        const w = toWorldRef.current(e.clientX, e.clientY);
        const dx = w.x - drag.grabX;
        const dy = w.y - drag.grabY;
        if (!drag.moved && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 3) return;
        drag.moved = true;
        setDragOffset({ ids: new Set(drag.origin.map((o) => o.id)), dx, dy });
      } else if (drag.kind === 'marquee') {
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return;
        if (!drag.moved && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 4) return;
        drag.moved = true;
        const x = Math.min(drag.startX, e.clientX) - rect.left;
        const y = Math.min(drag.startY, e.clientY) - rect.top;
        const w = Math.abs(e.clientX - drag.startX);
        const h = Math.abs(e.clientY - drag.startY);
        setMarquee({ x, y, w, h });
        const { pan: p, zoom: z } = viewRef.current;
        const x1 = (x - p.x) / z;
        const y1 = (y - p.y) / z;
        const x2 = (x + w - p.x) / z;
        const y2 = (y + h - p.y) / z;
        const hit = nodesRef.current
          .filter((n) => {
            const s = nodeSize(n, board.aspectRatio);
            return n.x < x2 && n.x + s.w > x1 && n.y < y2 && n.y + s.h > y1;
          })
          .map((n) => n.id);
        onSelectRef.current(Array.from(new Set([...drag.base, ...hit])));
      } else {
        setLinkTip(toWorldRef.current(e.clientX, e.clientY));
      }
    };
    const onUp = (e: PointerEvent) => {
      if (drag.kind === 'node' && drag.moved) {
        skipOpenRef.current = true;
        window.setTimeout(() => { skipOpenRef.current = false; }, 0);
        const w = toWorldRef.current(e.clientX, e.clientY);
        const dx = w.x - drag.grabX;
        const dy = w.y - drag.grabY;
        onMoveNodesRef.current(drag.origin.map((o) => ({ id: o.id, x: Math.round(o.x + dx), y: Math.round(o.y + dy) })));
      }
      if (drag.kind === 'link') {
        const ignore = new Set(drag.batch || [drag.from]);
        const to = nodeUnderPointer(e.clientX, e.clientY, ignore);
        const overNode = nodeUnderPointer(e.clientX, e.clientY, new Set());
        const at = toWorldRef.current(e.clientX, e.clientY);
        if (drag.batch) {
          const raw = document.elementFromPoint(e.clientX, e.clientY);
          const onCanvas = raw instanceof Element
            && Boolean(raw.closest('.tb-viewport'))
            && !raw.closest('.tb-zoom-bar, .tb-ctx, .tb-info, .tb-batch-anchor, .tb-prompt, .tb-input-mode');
          if (to) onConnectImagesRef.current?.(drag.batch, to, at);
          else if (!overNode && onCanvas) onConnectImagesRef.current?.(drag.batch, null, at);
        } else if (to && to !== drag.from) {
          const source = nodesRef.current.find((n) => n.id === drag.from);
          const target = nodesRef.current.find((n) => n.id === to);
          if (source?.kind === 'image' && target?.kind === 'video' && !target.edit && !target.fromGallery && onConnectImagesRef.current) {
            const chain = chainForVideo(boardRef.current, to).map((n) => n.id);
            const ids = chain.includes(drag.from) ? chain : [...chain, drag.from];
            onConnectImagesRef.current(ids, to, at, 'given');
          } else {
            onConnectRef.current(drag.from, to);
          }
        }
      }
      setDrag(null);
      setDragOffset(null);
      setMarquee(null);
      setLinkTip(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // The gesture object is frozen for this drag. Live helpers are refs so a parent
    // render cannot drop the pointer listeners mid-gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const pointerOrCenter = useCallback(() => {
    if (lastPointerRef.current) return lastPointerRef.current;
    const rect = viewportRef.current?.getBoundingClientRect();
    return rect ? toWorld(rect.left + rect.width / 2, rect.top + rect.height * 0.35) : { x: 0, y: 0 };
  }, [toWorld]);

  const runAction = useCallback((action: CanvasAction, at: { x: number; y: number }) => {
    setMenu(null);
    const accept = UPLOAD_ACCEPT[action];
    if (accept) {
      const input = fileInputRef.current;
      if (!input) return;
      uploadAtRef.current = at;
      input.accept = accept;
      input.value = '';
      input.click();
      return;
    }
    onActionRef.current?.(action, at);
  }, []);

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      if (isTextControl(e.target)) textBaseRef.current.set(e.target, e.target.value);
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);

  useEffect(() => {
    const textOwnsUndo = (el: EventTarget | null) => {
      if (el instanceof HTMLElement && el.isContentEditable) return true;
      if (!isTextControl(el)) return false;
      const base = textBaseRef.current.get(el);
      if (base == null) return el.value.length > 0;
      return el.value !== base;
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !readOnly) {
        const key = e.key.toLowerCase();
        const undoKey = (key === 'z' || e.code === 'KeyZ') && !e.shiftKey;
        const redoKey = key === 'y' || e.code === 'KeyY' || ((key === 'z' || e.code === 'KeyZ') && e.shiftKey);
        if (undoKey || redoKey) {
          if (!textOwnsUndo(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            if (undoKey) onUndoRef.current?.();
            else onRedoRef.current?.();
          }
          return;
        }
      }
      if (isTypingTarget(e.target)) return;
      if (e.key === ' ' && !readOnly) {
        if (!spaceRef.current) {
          spaceRef.current = true;
          setSpaceDown(true);
        }
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape') {
        setInfoId(null);
        setMenu(null);
      }
      if (readOnly) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'c') {
        if (!selectedIds.length && !selectedEdge) return;
        e.preventDefault();
        e.stopPropagation();
        onCopySelection?.(selectedIds, selectedEdge);
        return;
      }
      if (mod && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        e.stopPropagation();
        onPasteClipboard?.(pointerOrCenter());
        return;
      }
      const action = actionForKey(e);
      if (action) {
        e.preventDefault();
        runAction(action, pointerOrCenter());
        return;
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (selectedEdge) {
        onDeleteEdge(selectedEdge);
        setSelectedEdge(null);
        e.preventDefault();
      } else if (selectedIds.length) {
        onRemoveNodes(selectedIds);
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ') return;
      spaceRef.current = false;
      setSpaceDown(false);
    };
    const onBlur = () => {
      spaceRef.current = false;
      setSpaceDown(false);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [selectedEdge, selectedIds, onDeleteEdge, onRemoveNodes, onCopySelection, onPasteClipboard, readOnly, runAction, pointerOrCenter]);

  const [liveScale, setLiveScale] = useState<{ id: string; scale: number } | null>(null);
  const nodes = useMemo(
    () => board.nodes.map((n) => {
      let next = n;
      if (dragOffset?.ids.has(n.id)) next = { ...next, x: n.x + dragOffset.dx, y: n.y + dragOffset.dy };
      if (liveScale?.id === n.id) next = { ...next, scale: liveScale.scale };
      return next;
    }),
    [board.nodes, dragOffset, liveScale],
  );
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    if (readOnly) return;
    const at = toWorld(e.clientX, e.clientY);
    const gallery = e.dataTransfer.getData(GALLERY_DRAG_TYPE);
    if (gallery) {
      try {
        const items = JSON.parse(gallery) as GalleryDragItem[];
        if (items.length) {
          props.onDropGallery(items, at);
          return;
        }
      } catch {
        /* fall through to plain URL handling */
      }
    }
    const files = Array.from(e.dataTransfer.files || []).filter(isMediaFile);
    if (files.length) {
      props.onDropFiles(files, at);
      return;
    }
    const raw = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    const urls = raw.split(/\s+/).filter((u) => /^https?:\/\//i.test(u));
    if (urls.length) props.onDropUrls(urls, at);
  };

  const startNodeDrag = (e: React.PointerEvent, node: BoardNode) => {
    if (e.button !== 0 || spaceRef.current) return;
    if ((e.target as HTMLElement).closest('button, audio, input, select, textarea, .tb-handle, .tb-input-mode, a')) return;
    e.stopPropagation();
    capturePointer(e);
    setSelectedEdge(null);
    setMenu(null);
    let selection = selectedIds;
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      selection = selectedIds.includes(node.id) ? selectedIds.filter((id) => id !== node.id) : [...selectedIds, node.id];
      onSelect(selection);
    } else if (!selectedIds.includes(node.id)) {
      selection = [node.id];
      onSelect(selection);
    }
    if (readOnly) return;
    const moving = selection.includes(node.id) ? new Set(selection) : new Set([node.id]);
    const origin = board.nodes.filter((n) => moving.has(n.id)).map((n) => ({ id: n.id, x: n.x, y: n.y }));
    const grab = toWorld(e.clientX, e.clientY);
    setDrag({ kind: 'node', startX: e.clientX, startY: e.clientY, grabX: grab.x, grabY: grab.y, origin, moved: false });
  };

  const onResizeNodeRef = useRef(props.onResizeNode);
  onResizeNodeRef.current = props.onResizeNode;
  const resizeCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanup.current?.(), []);

  const startResize = (e: React.PointerEvent, node: BoardNode) => {
    if (e.button !== 0 || readOnly) return;
    e.stopPropagation();
    e.preventDefault();
    capturePointer(e);
    const origin = { x: node.x, y: node.y };
    const grab = toWorldRef.current(e.clientX, e.clientY);
    const fromCorner = Math.max(24, Math.hypot(grab.x - origin.x, grab.y - origin.y));
    const base = nodeScale(node);
    const apply = (clientX: number, clientY: number) => {
      const p = toWorldRef.current(clientX, clientY);
      const dist = Math.hypot(p.x - origin.x, p.y - origin.y);
      return clampNodeScale(base * (dist / fromCorner));
    };
    setLiveScale({ id: node.id, scale: base });
    const onMove = (ev: PointerEvent) => setLiveScale({ id: node.id, scale: apply(ev.clientX, ev.clientY) });
    const onUp = (ev: PointerEvent) => {
      resizeCleanup.current?.();
      resizeCleanup.current = null;
      const scale = apply(ev.clientX, ev.clientY);
      setLiveScale(null);
      onResizeNodeRef.current?.(node.id, scale);
    };
    resizeCleanup.current?.();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    resizeCleanup.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  };

  const batchImages = readOnly || !props.onConnectImages
    ? []
    : nodes.filter((n): n is BoardImageNode => n.kind === 'image' && selectedIds.includes(n.id));
  const batchIds = batchImages.length >= 2 ? batchImages.map((n) => n.id) : null;
  const batchAnchor = batchIds
    ? {
      x: Math.max(...batchImages.map((n) => n.x + nodeSize(n, board.aspectRatio).w)) + 56,
      y: (Math.min(...batchImages.map((n) => n.y)) + Math.max(...batchImages.map((n) => n.y + nodeSize(n, board.aspectRatio).h))) / 2,
    }
    : null;

  const startLink = (e: React.PointerEvent, node: BoardNode) => {
    e.stopPropagation();
    e.preventDefault();
    if (readOnly) return;
    capturePointer(e);
    setLinkTip(toWorld(e.clientX, e.clientY));
    const batch = batchIds?.includes(node.id) ? batchIds : undefined;
    setDrag({ kind: 'link', from: node.id, ...(batch ? { batch } : {}) });
  };

  const startBatchLink = (e: React.PointerEvent) => {
    if (!batchIds || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    capturePointer(e);
    setMenu(null);
    setSelectedEdge(null);
    setLinkTip(toWorld(e.clientX, e.clientY));
    setDrag({ kind: 'link', from: batchIds[0], batch: batchIds });
  };

  const linkBatch = drag?.kind === 'link' ? drag.batch : undefined;
  const linkFrom = drag?.kind === 'link' && !linkBatch ? byId.get(drag.from) : undefined;
  const bundleTo = linkBatch ? linkTip : batchAnchor;
  const bundleFrom = linkBatch
    ? linkBatch.map((id) => byId.get(id)).filter((n): n is BoardNode => Boolean(n))
    : batchImages;

  return (
    <div
      ref={viewportRef}
      className={`tb-viewport${drag?.kind === 'pan' ? ' is-panning' : ''}${drag?.kind === 'node' ? ' is-moving' : ''}${spaceDown ? ' is-space' : ''}${drag?.kind === 'marquee' ? ' is-selecting' : ''}${dropActive ? ' is-drop' : ''}${readOnly ? ' is-readonly' : ''}`}
      onPointerDown={(e) => {
        if (e.button !== 0 && e.button !== 1) return;
        const target = e.target as HTMLElement;
        if (target.closest('.tb-zoom-bar, .tb-edge-x, .tb-ctx')) return;
        setMenu(null);
        const panGesture = e.button === 1 || spaceRef.current || e.pointerType === 'touch';
        if (!panGesture && target.closest('.tb-node')) return;
        setSelectedEdge(null);
        if (panGesture) {
          e.preventDefault();
          capturePointer(e);
          stopZoomAnim();
          setDrag({ kind: 'pan', startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y });
          return;
        }
        const additive = e.shiftKey || e.metaKey || e.ctrlKey;
        if (!additive) onSelect([]);
        capturePointer(e);
        setDrag({ kind: 'marquee', startX: e.clientX, startY: e.clientY, base: additive ? selectedIds : [], moved: false });
      }}
      onPointerMove={(e) => {
        lastPointerRef.current = toWorld(e.clientX, e.clientY);
      }}
      onPointerLeave={() => {
        lastPointerRef.current = null;
      }}
      onContextMenu={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.tb-node, .tb-ctx, .tb-zoom-bar, .tb-info')) return;
        e.preventDefault();
        if (readOnly) return;
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, at: toWorld(e.clientX, e.clientY) });
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!dropActive && !readOnly) setDropActive(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDropActive(false);
      }}
      onDrop={handleDrop}
      style={{ backgroundPosition: `${pan.x}px ${pan.y}px`, backgroundSize: `${24 * zoom}px ${24 * zoom}px` }}
    >
      <div className="tb-world" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
        <svg className="tb-edges" width="1" height="1">
          <defs>
            <marker id="tb-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#818cf8" />
            </marker>
            <marker id="tb-arrow-join" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#34d399" />
            </marker>
            <marker id="tb-arrow-cut" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#fbbf24" />
            </marker>
            <marker id="tb-arrow-audio" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#e879f9" />
            </marker>
            <marker id="tb-arrow-ref" viewBox="0 0 12 12" refX="10" refY="6" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
              <path d="M 1 6 L 6 1.5 L 11 6 L 6 10.5 Z" fill="none" stroke="#22d3ee" strokeWidth="1.6" />
            </marker>
          </defs>
          {board.edges.map((edge) => {
            const a = byId.get(edge.from);
            const b = byId.get(edge.to);
            if (!a || !b) return null;
            const dy = referenceLane(board.edges, edge);
            const d = edgePath(a, b, board.aspectRatio, dy);
            const join = a.kind === 'video' && b.kind === 'video';
            const sound = a.kind === 'audio';
            const refEdge = edge.role === 'ref';
            const cut = !refEdge && sceneTransition(a, b) === 'cut';
            const selected = selectedEdge === edge.id;
            const marker = refEdge
              ? 'url(#tb-arrow-ref)'
              : sound ? 'url(#tb-arrow-audio)' : join ? 'url(#tb-arrow-join)' : cut ? 'url(#tb-arrow-cut)' : 'url(#tb-arrow)';
            // One-shot travel on new links only. Infinite per-edge blur loops crush the GPU when many
            // references feed a generating video — the node ring already shows "working".
            const showEnergy = freshEdges.has(edge.id) && !cut && !refEdge;
            return (
              <g key={edge.id} className={`tb-edge${join ? ' is-join' : ''}${sound ? ' is-audio' : ''}${refEdge ? ' is-ref' : ''}${cut ? ' is-cut' : ''}${selected ? ' is-selected' : ''}${showEnergy ? ' is-fresh' : ''}`}>
                <path d={d} className="tb-edge-hit" onPointerDown={(e) => { e.stopPropagation(); setSelectedEdge(edge.id); onSelect([]); }}>
                  {refEdge ? <title>Referência da geração. Esta seta não entra no vídeo.</title> : null}
                </path>
                <path d={d} className="tb-edge-line" markerEnd={marker} />
                {showEnergy && (
                  <>
                    <path d={d} className="tb-edge-energy tb-edge-energy-haze" pathLength={100} />
                    <path d={d} className="tb-edge-energy tb-edge-energy-bloom" pathLength={100} />
                    <path d={d} className="tb-edge-energy tb-edge-energy-core" pathLength={100} />
                  </>
                )}
              </g>
            );
          })}
          {linkFrom && linkTip && (
            <path
              className="tb-edge-line is-draft"
              d={`M ${linkFrom.x + nodeSize(linkFrom, board.aspectRatio).w} ${linkFrom.y + nodeSize(linkFrom, board.aspectRatio).h / 2} L ${linkTip.x} ${linkTip.y}`}
            />
          )}
          {bundleTo && bundleFrom.map((n) => {
            const x1 = n.x + nodeSize(n, board.aspectRatio).w;
            const y1 = n.y + nodeSize(n, board.aspectRatio).h / 2;
            const dx = Math.max(24, Math.abs(bundleTo.x - x1) * 0.45);
            return (
              <path
                key={`b-${n.id}`}
                className={`tb-edge-line ${linkBatch ? 'is-draft' : 'is-bundle'}`}
                d={`M ${x1} ${y1} C ${x1 + dx} ${y1}, ${bundleTo.x - dx} ${bundleTo.y}, ${bundleTo.x} ${bundleTo.y}`}
              />
            );
          })}
        </svg>

        {board.edges.map((edge) => {
          const a = byId.get(edge.from);
          const b = byId.get(edge.to);
          const kind = a && b && edge.role !== 'ref' ? sceneTransition(a, b) : null;
          if (!a || !b || !kind) return null;
          const x = (a.x + nodeSize(a, board.aspectRatio).w + b.x) / 2;
          const y = (a.y + nodeSize(a, board.aspectRatio).h / 2 + b.y + nodeSize(b, board.aspectRatio).h / 2) / 2;
          const cut = kind === 'cut';
          return (
            <span
              key={`t-${edge.id}`}
              className={`tb-edge-tag${cut ? ' is-cut' : ''}`}
              style={{ left: x, top: y - 20 }}
              title={cut ? 'Corte seco entre estas cenas' : 'Cena contínua (mesmo plano, sem corte)'}
            >
              {cut ? <Scissors size={10} /> : <MoveRight size={10} />}
              {cut ? 'Corte' : 'Contínua'}
            </span>
          );
        })}

        {board.edges.map((edge) => {
          const a = byId.get(edge.from);
          const b = byId.get(edge.to);
          if (a?.kind !== 'video' || b?.kind !== 'video' || b.edit || !b.joinTransition) return null;
          const at = edgePoint(a, b, 0.28, board.aspectRatio);
          const t = b.joinTransition;
          return (
            <span
              key={`f-${edge.id}`}
              className="tb-edge-tag is-fade"
              style={{ left: at.x, top: at.y }}
              title={t.kind === 'crossfade'
                ? `Transição suave de ${secsPt(t.seconds)} entre este vídeo e o próximo na junção`
                : `Fade pelo preto de ${secsPt(t.seconds)} na entrada e na saída deste vídeo na junção`}
            >
              <Blend size={10} />
              {joinTransitionLabel(t)}
            </span>
          );
        })}

        {board.edges.map((edge) => {
          if (edge.role !== 'ref') return null;
          const a = byId.get(edge.from);
          const b = byId.get(edge.to);
          if (!a || !b || a.kind !== 'image' || b.kind !== 'image') return null;
          const dy = referenceLane(board.edges, edge);
          const at = edgePoint(a, b, 0.55, board.aspectRatio, dy);
          return (
            <span
              key={`r-${edge.id}`}
              className="tb-edge-tag is-ref"
              style={{ left: at.x, top: at.y }}
              title="Referência da geração. Esta seta não entra no vídeo. A seta contínua lilás é a que gera o vídeo."
            >
              <Images size={10} />
              Referência
            </span>
          );
        })}

        {selectedEdge && !readOnly && (() => {
          const edge = board.edges.find((e) => e.id === selectedEdge);
          const a = edge && byId.get(edge.from);
          const b = edge && byId.get(edge.to);
          if (!edge || !a || !b) return null;
          const x = (a.x + nodeSize(a, board.aspectRatio).w + b.x) / 2;
          const y = (a.y + nodeSize(a, board.aspectRatio).h / 2 + b.y + nodeSize(b, board.aspectRatio).h / 2) / 2;
          return (
            <button
              type="button"
              className="tb-edge-x"
              style={{ left: x, top: y }}
              title="Remover seta (Delete)"
              aria-label="Remover seta"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => { onDeleteEdge(edge.id); setSelectedEdge(null); }}
            >
              <X size={12} />
            </button>
          );
        })()}

        {nodes.map((node) => {
          const common: NodeViewProps = {
            selected: selectedIds.includes(node.id),
            linking: drag?.kind === 'link' && drag.from !== node.id && !drag.batch?.includes(node.id),
            receiving: freshNodes.has(node.id),
            readOnly,
            infoOpen: infoId === node.id,
            onPointerDown: (e) => startNodeDrag(e, node),
            onResize: readOnly ? undefined : (e) => startResize(e, node),
            scale: nodeScale(node),
            zoom,
            onStartLink: (e) => startLink(e, node),
            onRemove: () => onRemoveNodes([node.id]),
            onInfo: () => setInfoId((cur) => (cur === node.id ? null : node.id)),
            onOpen: props.onOpenMedia && node.kind !== 'audio'
              ? (e) => {
                  if (skipOpenRef.current) return;
                  const target = e.target as HTMLElement;
                  const expand = Boolean(target.closest('.tb-gen-expand'));
                  if (!expand && target.closest('button, input, select, textarea, audio, .tb-handle, .tb-video-versions, .tb-mode-toggle, .tb-gen-panel, .tb-gen-rail')) return;
                  if (target instanceof HTMLVideoElement) target.pause();
                  e.preventDefault();
                  props.onOpenMedia?.(node.id);
                }
              : undefined,
          };
          if (node.kind === 'comment') {
            return (
              <CommentNodeView
                key={node.id}
                node={node}
                selected={common.selected}
                readOnly={readOnly}
                zoom={zoom}
                autoFocus={props.focusCommentId === node.id}
                onFocused={props.onCommentFocused}
                onPointerDown={common.onPointerDown}
                onRemove={common.onRemove}
                onPatch={(patch) => props.onUpdateComment?.(node.id, patch)}
                onActivate={() => onSelect(common.selected ? selectedIds : [node.id])}
              />
            );
          }
          return node.kind === 'image' ? (
            <ImageNodeView
              key={node.id}
              {...common}
              node={node}
              scene={sceneIndex.get(node.id)}
              role={props.sceneRole?.get(node.id)}
              composed={board.edges.some((e) => e.role === 'ref' && e.to === node.id && byId.get(e.from)?.kind === 'image')}
              onSelectVersion={props.onSelectImageVersion ? (versionId) => props.onSelectImageVersion?.(node.id, versionId) : undefined}
              assembling={props.assemblingIds?.includes(node.id)}
            />
          ) : node.kind === 'audio' ? (
            <AudioNodeView
              key={node.id}
              {...common}
              node={node}
              onSelectVersion={(versionId) => props.onSelectAudioVersion(node.id, versionId)}
              onUpdate={(patch) => props.onUpdateAudio(node.id, patch)}
              onRegenerate={() => props.onRegenerateAudio(node.id)}
            />
          ) : (
            <VideoNodeView
              key={node.id}
              {...common}
              node={node}
              meta={videoMeta.get(node.id)}
              projectTitle={props.projectTitle}
              onSelectVersion={(versionId) => props.onSelectVersion(node.id, versionId)}
              onSetImageMode={props.onSetImageMode ? (mode) => props.onSetImageMode?.(node.id, mode) : undefined}
              onPatch={props.onPatchVideo ? (patch) => props.onPatchVideo?.(node.id, patch) : undefined}
              onUpload={props.onUploadGenerator ? (slot, file) => props.onUploadGenerator?.(node.id, slot, file) : undefined}
              onGenerate={props.onGenerateVideo ? () => props.onGenerateVideo?.(node.id) : undefined}
              assembling={props.assemblingIds?.includes(node.id)}
              aspect={props.aspect}
              onAspect={props.onAspect}
            />
          );
        })}

        {batchIds && batchAnchor && !linkBatch && (
          <button
            type="button"
            className="tb-batch-anchor"
            style={{ left: batchAnchor.x, top: batchAnchor.y }}
            title="Arraste até um vídeo para ligar todas as imagens selecionadas de uma vez (ou solte no vazio para criar um vídeo)"
            onPointerDown={startBatchLink}
          >
            <span className="tb-batch-dot" aria-hidden />
            Ligar {batchIds.length} imagens
          </button>
        )}
      </div>

      {marquee && (
        <div className="tb-marquee" style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }} />
      )}

      {menu && (
        <CanvasContextMenu
          x={menu.x}
          y={menu.y}
          bounds={{ w: viewportRef.current?.clientWidth || 1200, h: viewportRef.current?.clientHeight || 800 }}
          onPick={(action) => runAction(action, menu.at)}
          onClose={() => setMenu(null)}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []).filter(isMediaFile);
          if (files.length) props.onDropFiles(files, uploadAtRef.current);
          e.target.value = '';
        }}
      />

      {infoNode && (
        <CanvasNodeInfo
          board={board}
          node={infoNode}
          sceneIndex={sceneIndex}
          style={infoStyle(infoNode)}
          onClose={() => setInfoId(null)}
        />
      )}

      {!board.nodes.length && !readOnly && (
        <div className="tb-empty">
          <ImagePlus size={28} strokeWidth={1.5} />
          <strong>Arraste imagens para o quadro</strong>
          <span>ou</span>
          <button
            type="button"
            className="tb-empty-btn"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={props.onOpenGallery}
          >
            Abrir a galeria
          </button>
          <span>ou clique com o botão direito para carregar, gerar ou comentar</span>
        </div>
      )}

      <div className="tb-zoom-bar" onPointerDown={(e) => e.stopPropagation()}>
        {!readOnly && props.onUndo && (
          <>
            <button type="button" className="tb-zoom-btn" aria-label="Desfazer" title="Desfazer a última ação do quadro (Ctrl+Z)" disabled={!props.canUndo} onClick={props.onUndo}>
              <Undo2 size={14} />
            </button>
            <button type="button" className="tb-zoom-btn" aria-label="Refazer" title="Refazer (Ctrl+Y)" disabled={!props.canRedo} onClick={props.onRedo}>
              <Redo2 size={14} />
            </button>
            <span className="tb-zoom-sep" />
          </>
        )}
        <button type="button" className="tb-zoom-btn" aria-label="Diminuir zoom" onClick={() => zoomFromCenter(1 / 1.2)}>
          <Minus size={14} />
        </button>
        <span className="tb-zoom-label">{Math.round(zoom * 100)}%</span>
        <button type="button" className="tb-zoom-btn" aria-label="Aumentar zoom" onClick={() => zoomFromCenter(1.2)}>
          <Plus size={14} />
        </button>
        <button type="button" className="tb-zoom-btn" aria-label="Enquadrar tudo" title="Enquadrar tudo" onClick={fitView}>
          <Maximize2 size={13} />
        </button>
        {!readOnly && props.onLayout && (
          <button
            type="button"
            className="tb-zoom-btn"
            aria-label="Organizar o fluxo"
            title="Organizar o fluxo"
            disabled={!board.nodes.length}
            onClick={props.onLayout}
          >
            <AlignHorizontalDistributeCenter size={14} />
          </button>
        )}
      </div>
    </div>
  );
});

export default BoardCanvas;

type NodeViewProps = {
  selected: boolean;
  linking: boolean;
  receiving: boolean;
  readOnly?: boolean;
  infoOpen: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onResize?: (e: React.PointerEvent) => void;
  /** Current visual scale, so chrome that must stay grabbable can cancel it. */
  scale?: number;
  zoom?: number;
  onStartLink: (e: React.PointerEvent) => void;
  onRemove: () => void;
  onInfo: () => void;
  onOpen?: (e: React.MouseEvent) => void;
  inputMode?: BoardImageMode;
  onInputMode?: (mode: BoardImageMode) => void;
  inputModeDisabled?: boolean;
};

const INPUT_MODES: Array<{ id: BoardImageMode; label: string; title: string }> = [
  {
    id: 'keyframes',
    label: 'Contínuo',
    title: 'Uma imagem liga na próxima, inclusive quadros-chave, e a última liga neste vídeo',
  },
  {
    id: 'reference',
    label: 'Referências',
    title: 'Todas as imagens ligadas entram direto neste vídeo, sem ordem entre elas',
  },
];

function ReceiveRing({ on, loop }: { on: boolean; loop?: boolean }) {
  if (!on) return null;
  return (
    <svg className={`tb-receive-ring${loop ? ' is-loop' : ''}`} aria-hidden="true">
      <rect className="tb-receive-haze" pathLength={100} />
      <rect className="tb-receive-bloom" pathLength={100} />
      <rect className="tb-receive-line" pathLength={100} />
    </svg>
  );
}

function VideoInputModeButtons({
  mode,
  onMode,
  disabled,
  className = 'tb-input-mode',
}: {
  mode: BoardImageMode;
  onMode: (m: BoardImageMode) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <span className={className} role="radiogroup" aria-label="Entrada do vídeo" onPointerDown={(e) => e.stopPropagation()}>
      {INPUT_MODES.map((item) => (
        <button
          key={item.id}
          type="button"
          role="radio"
          aria-checked={mode === item.id}
          className={`${mode === item.id ? 'is-on' : ''}${item.id === 'reference' ? ' is-reference' : ''}`}
          title={item.title}
          disabled={disabled}
          onClick={() => onMode(item.id)}
        >
          {item.label}
        </button>
      ))}
    </span>
  );
}

function NodeChrome({
  selected, linking, readOnly, infoOpen, onStartLink, onRemove, onInfo, onResize, scale = 1, zoom = 1, inputMode, onInputMode, inputModeDisabled,
}: NodeViewProps) {
  return (
    <>
      <span className={`tb-handle tb-handle--in${linking ? ' is-target' : ''}${inputMode === 'reference' ? ' is-reference' : ''}`} aria-hidden />
      {onInputMode && inputMode && (
        <VideoInputModeButtons mode={inputMode} onMode={onInputMode} disabled={inputModeDisabled} />
      )}
      {!readOnly && (
        <span
          className="tb-handle tb-handle--out"
          title="Arraste para ligar"
          onPointerDown={onStartLink}
        />
      )}
      <button
        type="button"
        className={`tb-node-info${infoOpen ? ' is-open' : ''}`}
        aria-label="Ver roteiro"
        title="Ver roteiro"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onInfo}
      >
        <Info size={12} />
      </button>
      {selected && !readOnly && (
        <button type="button" className="tb-node-remove" aria-label="Remover" title="Remover (Delete)" onClick={onRemove}>
          <X size={11} />
        </button>
      )}
      {selected && !readOnly && onResize && (
        <button
          type="button"
          className="tb-node-resize"
          aria-label="Diminuir o nó"
          title="Arraste para diminuir o nó"
          style={{ transform: `scale(${1 / Math.max(0.2, scale * zoom)})` }}
          onPointerDown={onResize}
        />
      )}
    </>
  );
}

const ROLE_CHIPS: Record<SceneImageRole, { label: string; title: string }> = {
  start: { label: 'Início', title: 'Quadro-chave: o vídeo começa exatamente nesta imagem' },
  end: { label: 'Fim', title: 'Quadro-chave: o vídeo termina exatamente nesta imagem' },
  keyframe: { label: 'Passagem', title: 'Quadro-chave: o vídeo passa por esta imagem, na ordem das setas' },
  reference: { label: 'Referência', title: 'Referência de estilo/lugar: guia o visual, sem posição fixa no tempo' },
};

function CommentNodeView({
  node, selected, readOnly, zoom, autoFocus, onFocused, onPointerDown, onRemove, onPatch, onActivate,
}: {
  node: BoardCommentNode;
  selected: boolean;
  readOnly?: boolean;
  zoom: number;
  autoFocus?: boolean;
  onFocused?: () => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onRemove: () => void;
  onPatch: (patch: { text?: string; w?: number; h?: number }) => void;
  onActivate: () => void;
}) {
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState<{ w: number; h: number } | null>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!autoFocus || focused.current) return;
    focused.current = true;
    textRef.current?.focus();
    onFocused?.();
  }, [autoFocus, onFocused]);

  const resizeCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanup.current?.(), []);

  const startResize = (e: React.PointerEvent) => {
    if (e.button !== 0 || readOnly) return;
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = nodeFrame(node);
    const z = Math.max(0.2, zoom);
    const apply = (clientX: number, clientY: number) => clampCommentBox(
      base.w + (clientX - startX) / z,
      base.h + (clientY - startY) / z,
    );
    const onMove = (ev: PointerEvent) => setDraft(apply(ev.clientX, ev.clientY));
    const onUp = (ev: PointerEvent) => {
      resizeCleanup.current?.();
      resizeCleanup.current = null;
      const next = apply(ev.clientX, ev.clientY);
      setDraft(null);
      if (next.w !== base.w || next.h !== base.h) onPatch({ w: next.w, h: next.h });
    };
    resizeCleanup.current?.();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    resizeCleanup.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  };

  const box = nodeBoxStyle(node);
  return (
    <div
      data-node-id={node.id}
      className={`tb-node tb-node--comment${selected ? ' is-selected' : ''}`}
      style={draft ? { ...box, width: draft.w, height: draft.h } : box}
      onPointerDown={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('textarea, button')) {
          e.stopPropagation();
          if (target.closest('textarea')) onActivate();
          return;
        }
        onPointerDown(e);
      }}
    >
      <div className="tb-comment-head">
        <MessageSquare size={13} />
        Comentário
      </div>
      <textarea
        ref={textRef}
        className="tb-comment-text"
        value={node.text}
        readOnly={readOnly}
        maxLength={COMMENT_TEXT_MAX}
        placeholder="Escreva uma nota para o fluxo…"
        aria-label="Texto do comentário"
        onChange={(e) => onPatch({ text: e.target.value.slice(0, COMMENT_TEXT_MAX) })}
      />
      {selected && !readOnly && (
        <button type="button" className="tb-node-remove" aria-label="Remover" title="Remover (Delete)" onClick={onRemove}>
          <X size={11} />
        </button>
      )}
      {selected && !readOnly && (
        <button
          type="button"
          className="tb-node-resize"
          aria-label="Redimensionar comentário"
          title="Arraste para mudar o tamanho da caixa"
          style={{ transform: `scale(${1 / Math.max(0.2, zoom)})` }}
          onPointerDown={startResize}
        />
      )}
    </div>
  );
}

function ImageNodeView(props: NodeViewProps & {
  node: BoardImageNode;
  scene?: number;
  role?: SceneImageRole;
  composed?: boolean;
  onSelectVersion?: (versionId: string) => void;
  assembling?: boolean;
}) {
  const { node, scene, role, composed, selected, linking, receiving, readOnly, onPointerDown, onSelectVersion, assembling } = props;
  const p = node.phase;
  const pending = !p.imageUrl || p.status === 'pending' || p.status === 'failed';
  const chip = role ? ROLE_CHIPS[role] : undefined;
  const versions = imageVersions(node);
  const activeIdx = versions.findIndex((v) => v.imageUrl === p.imageUrl);
  const busy = p.status === 'generating' || !!readOnly || !onSelectVersion;
  const versionTitle = (i: number) => (i === 0 ? 'Original' : versions[i]?.instruction || `Versão ${i + 1}`);
  return (
    <div
      data-node-id={node.id}
      className={`tb-node tb-node--image${selected ? ' is-selected' : ''}${pending ? ' is-pending' : ''}${linking ? ' is-link-target' : ''}${receiving ? ' is-receive' : ''}`}
      style={nodeBoxStyle(node)}
      onPointerDown={onPointerDown}
      onDoubleClick={props.onOpen}
    >
      <ReceiveRing on={receiving} />
      <div className="tb-node-media">
        {chip && (
          <span className={`tb-role-chip is-${role}`} title={chip.title}>{chip.label}</span>
        )}
        {p.imageUrl && <img src={p.imageUrl} alt={p.label} draggable={false} />}
        {!p.imageUrl && p.status !== 'generating' && (
          <div className="tb-node-placeholder">
            {p.status === 'failed' ? <AlertTriangle size={16} /> : <Sparkles size={16} />}
            <span>{p.status === 'failed' ? 'Falhou' : composed ? 'Imagem a gerar' : role === 'reference' ? 'Referência a gerar' : 'Quadro-chave a gerar'}</span>
          </div>
        )}
        {(p.status === 'generating' || assembling) && (
          <div className="tb-node-loading">
            <PixelAssemble label={p.status === 'generating' ? (p.error || 'Gerando…') : 'Preparando…'} />
          </div>
        )}
      </div>
      <div className="tb-node-meta" title={p.description_pt}>
        {scene != null && <span className="tb-node-index">{scene}</span>}
        <span className="tb-node-label">{p.label}</span>
        {versions.length > 1 && (
          <span
            className="tb-video-versions"
            title={activeIdx >= 0 ? versionTitle(activeIdx) : undefined}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Versão anterior"
              title={activeIdx > 0 ? versionTitle(activeIdx - 1) : undefined}
              disabled={busy || activeIdx <= 0}
              onClick={() => onSelectVersion?.(versions[activeIdx - 1].id)}
            >
              <ChevronLeft size={13} />
            </button>
            <span>{activeIdx >= 0 ? `v${activeIdx + 1}` : '—'}/{versions.length}</span>
            <button
              type="button"
              aria-label="Próxima versão"
              title={activeIdx >= 0 && activeIdx < versions.length - 1 ? versionTitle(activeIdx + 1) : undefined}
              disabled={busy || activeIdx < 0 || activeIdx >= versions.length - 1}
              onClick={() => onSelectVersion?.(versions[activeIdx + 1].id)}
            >
              <ChevronRight size={13} />
            </button>
          </span>
        )}
      </div>
      <NodeChrome {...props} />
    </div>
  );
}

const AUDIO_ICONS = { voice: Mic, music: Music, sfx: AudioLines } as const;

function AudioNodeView(props: NodeViewProps & {
  node: BoardAudioNode;
  onSelectVersion: (versionId: string) => void;
  onUpdate: (patch: AudioTrackPatch) => void;
  onRegenerate: () => void;
}) {
  const { node, selected, linking, receiving, readOnly, onPointerDown, onUpdate } = props;
  const busy = node.status === 'generating' || !!readOnly;
  const versions = audioVersions(node);
  const activeIdx = versions.findIndex((v) => v.audioUrl === node.audioUrl);
  const [volume, setVolume] = useState(node.volume);
  useEffect(() => setVolume(node.volume), [node.volume]);
  const commitVolume = () => {
    if (Math.abs(volume - node.volume) > 0.005) onUpdate({ volume });
  };
  const Icon = AUDIO_ICONS[node.audioKind];
  const info = [
    node.seconds ? `${node.seconds.toFixed(1)}s` : null,
    node.startOffset ? `início ${node.startOffset}s` : null,
    node.speed && node.speed !== 1 ? `${node.speed}×` : null,
    node.loop ? 'loop' : null,
  ].filter(Boolean).join(' · ');
  return (
    <div
      data-node-id={node.id}
      className={`tb-node tb-node--audio is-${node.audioKind}${selected ? ' is-selected' : ''}${linking ? ' is-link-target' : ''}${receiving ? ' is-receive' : ''}${node.muted ? ' is-muted' : ''}`}
      style={nodeBoxStyle(node)}
      onPointerDown={onPointerDown}
      title={node.text}
    >
      <ReceiveRing on={receiving} />
      <div className="tb-audio-head">
        <span className="tb-audio-title">
          <Icon size={14} /> <span>{node.title || 'Som'}</span>
        </span>
        {versions.length > 1 ? (
          <span className="tb-video-versions" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Versão anterior" disabled={busy || activeIdx <= 0} onClick={() => props.onSelectVersion(versions[activeIdx - 1].id)}>
              <ChevronLeft size={13} />
            </button>
            <span>{activeIdx >= 0 ? `v${activeIdx + 1}` : '—'}/{versions.length}</span>
            <button
              type="button"
              aria-label="Próxima versão"
              disabled={busy || activeIdx < 0 || activeIdx >= versions.length - 1}
              onClick={() => props.onSelectVersion(versions[activeIdx + 1].id)}
            >
              <ChevronRight size={13} />
            </button>
          </span>
        ) : info ? <span className="tb-video-info">{info}</span> : null}
      </div>
      <div className="tb-audio-body">
        {node.status === 'generating' && (
          <span className="tb-audio-status"><Loader2 size={13} className="spin" /> {node.progress || 'Gerando…'}</span>
        )}
        {node.status !== 'generating' && node.audioUrl && <audio src={node.audioUrl} controls preload="none" />}
        {node.status !== 'generating' && !node.audioUrl && readOnly && (
          <span className="tb-audio-status">{node.status === 'failed' ? 'Falhou' : 'Ainda não gerado'}</span>
        )}
        {!busy && !node.audioUrl && (
          <button type="button" className={`tb-audio-status is-action${node.status === 'failed' ? ' is-error' : ''}`} onClick={props.onRegenerate}>
            {node.status === 'failed' ? <AlertTriangle size={13} /> : <Sparkles size={13} />}
            {node.status === 'failed' ? node.error || 'Falhou — gerar de novo' : 'Escolher modelo e gerar'}
          </button>
        )}
      </div>
      <div className="tb-audio-controls" onPointerDown={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={node.muted ? 'Ativar som' : 'Silenciar'}
          title={node.muted ? 'Ativar som' : 'Silenciar'}
          disabled={readOnly}
          onClick={() => onUpdate({ muted: !node.muted })}
        >
          {node.muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
        </button>
        <input
          type="range"
          min={0}
          max={1.5}
          step={0.05}
          value={volume}
          disabled={readOnly}
          aria-label="Volume"
          onChange={(e) => setVolume(Number(e.target.value))}
          onPointerUp={commitVolume}
          onKeyUp={commitVolume}
        />
        <span className="tb-audio-vol">{Math.round(volume * 100)}%</span>
        {node.audioUrl && (
          <button type="button" aria-label="Gerar nova versão" title="Gerar nova versão" disabled={busy} onClick={props.onRegenerate}>
            <RefreshCw size={12} />
          </button>
        )}
      </div>
      <NodeChrome {...props} />
    </div>
  );
}

const REFS_SHOWN = 8;

const GEN_MODELS = [
  ...listModelsForModality('i2v'),
  ...listModelsForModality('v2v').filter((m) => !m.modalities.includes('i2v')),
];

function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** What the prompt box shows: the user's own text while they are writing it, otherwise the full script prompt. */
function generatorPrompt(node: BoardVideoNode, meta?: VideoMeta): string {
  const g = node.generator;
  if (g?.promptSource === 'user' && g.prompt != null) return g.prompt;
  return meta?.prompt || g?.prompt || node.requests?.[node.requests.length - 1] || node.brief || '';
}

type GenOption = { id: string; label: string };

const GEN_MENU_MAX_H = 260;

/** Themed dropdown for the generator card (the native <select> ignores the canvas theme and clips inside the node). */
function GenSelect(props: {
  label: string;
  title?: string;
  value: string;
  options: GenOption[];
  disabled?: boolean;
  /** Lets this chip take more of the row (model names are the longest). */
  wide?: boolean;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const current = props.options.find((o) => o.id === props.value);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const up = window.innerHeight - r.bottom < GEN_MENU_MAX_H + 12 && r.top > GEN_MENU_MAX_H;
    setPos({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: Math.max(r.width, 160), up });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('wheel', close, { passive: true });
    menuRef.current?.querySelector<HTMLElement>('.is-on')?.scrollIntoView({ block: 'nearest' });
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('wheel', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`tb-gen-select${open ? ' is-open' : ''}${props.wide ? ' is-wide' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={props.label}
        title={props.title || current?.label || props.label}
        disabled={props.disabled || !props.options.length}
        onClick={toggle}
      >
        <span>{current?.label || '—'}</span>
        <ChevronDown size={11} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="tb-gen-menu"
          role="listbox"
          aria-label={props.label}
          style={{ left: pos.left, top: pos.top, minWidth: pos.width, transform: pos.up ? 'translateY(-100%)' : undefined }}
          onWheel={(e) => e.stopPropagation()}
        >
          {props.options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="option"
              aria-selected={o.id === props.value}
              className={o.id === props.value ? 'is-on' : ''}
              onClick={() => { props.onChange(o.id); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

function GeneratorNodeView(props: NodeViewProps & {
  node: BoardVideoNode;
  meta?: VideoMeta;
  projectTitle?: string;
  onSelectVersion: (versionId: string) => void;
  onSetImageMode?: (mode: BoardImageMode) => void;
  onPatch?: (patch: Partial<BoardVideoNode>) => void;
  onUpload?: (slot: GeneratorSlot, file: File) => void;
  onGenerate?: () => void;
  aspect?: BoardAspect;
  onAspect?: (aspect: BoardAspect) => void;
  assembling?: boolean;
}) {
  const { node, meta, selected, linking, receiving, readOnly, onPointerDown, assembling } = props;
  const busy = node.status === 'generating' || node.status === 'joining';
  const settled = Boolean(node.videoUrl) && !busy;
  const locked = busy || !!readOnly;
  const gen: BoardGenerator = node.generator || {};
  const hidePrompt = Boolean(gen.hidePrompt);
  const locks = gen.locks || {};
  const lockTip = 'Definido pelo modelo';
  const fileRef = useRef<HTMLInputElement>(null);
  const slotRef = useRef<GeneratorSlot>('attachment');
  const savedPrompt = generatorPrompt(node, meta);
  const showsScript = Boolean(meta?.prompt) && gen.promptSource !== 'user';
  const [prompt, setPrompt] = useState(savedPrompt);
  const [uploading, setUploading] = useState(false);
  useEffect(() => setPrompt(savedPrompt), [savedPrompt]);

  const modelId = node.videoModelId && getVideoModelById(node.videoModelId) ? node.videoModelId : (GEN_MODELS[0]?.id || '');
  const model = getVideoModelById(modelId);
  const resolutionId = model?.resolutions.some((r) => r.id === node.resolutionId)
    ? node.resolutionId!
    : (model?.resolutions.find((r) => r.id === '720p')?.id || model?.resolutions[0]?.id || '');
  const scriptSeconds = scriptOwnedSeconds(node.planned, meta?.seconds);
  const duration = scriptSeconds ?? Math.min(
    model?.durationMax || 10,
    Math.max(model?.durationMin || 1, Math.round(node.totalDurationSeconds || 10)),
  );
  const durationTitle = scriptSeconds ? scriptDurationTitle(scriptSeconds) : undefined;
  const soundOn = model && alwaysGeneratedAudio(model) ? true : Boolean(node.generateAudio);
  const audioChoice = Boolean(model && supportsGeneratedAudio(model) && !alwaysGeneratedAudio(model));
  const hasImages = Boolean(
    gen.startImage?.url || gen.endImage?.url || gen.attachments?.some((a) => a.url) || meta?.refs?.some((r) => r.url),
  );
  const hasRefVideo = Boolean(gen.referenceVideo?.url);
  const v2vOk = Boolean(model?.modalities.includes('v2v'));
  const canGenerate = !locked && (hasImages || (hasRefVideo && prompt.trim().length > 0 && v2vOk));

  /** Anything typed in the box is the user's request; an untouched script stays the script. */
  const promptPatch = (): Partial<BoardVideoNode> => {
    if (prompt.trim() === savedPrompt.trim()) return {};
    return { brief: prompt, generator: { ...gen, prompt, promptSource: 'user' } };
  };
  const savePrompt = () => {
    if (locked) return;
    const patch = promptPatch();
    if (Object.keys(patch).length) props.onPatch?.(patch);
  };
  const patchGen = (partial: Partial<BoardGenerator>, extra?: Partial<BoardVideoNode>) => {
    props.onPatch?.({ ...extra, generator: { ...gen, ...partial } });
  };
  const setModel = (id: string) => {
    const next = getVideoModelById(id);
    if (!next) return;
    const res = next.resolutions.find((r) => r.id === resolutionId) || next.resolutions.find((r) => r.id === '720p') || next.resolutions[0];
    const seconds = Math.min(next.durationMax || duration, Math.max(next.durationMin || 1, duration));
    props.onPatch?.({
      videoModelId: next.id,
      resolutionId: res?.id,
      totalDurationSeconds: seconds,
      generateAudio: defaultGenerateAudio(next, res),
    });
  };
  const openSlot = (slot: GeneratorSlot) => {
    if (locked || !props.onUpload) return;
    slotRef.current = slot;
    const input = fileRef.current;
    if (!input) return;
    input.accept = slot === 'video' ? 'video/*' : slot === 'audio' ? 'audio/*' : 'image/*';
    input.multiple = slot === 'attachment';
    input.click();
  };
  const onFiles = (list: FileList | File[]) => {
    const files = Array.from(list);
    const slot = slotRef.current;
    if (!files.length || !props.onUpload) return;
    setUploading(true);
    Promise.all(files.map((f) => Promise.resolve(props.onUpload?.(slot, f)))).finally(() => setUploading(false));
  };
  const clearRef = (key: 'startImage' | 'endImage' | 'referenceVideo' | 'audio') => {
    patchGen({ [key]: undefined });
  };
  const dropRef = (attachment: BoardMediaRef) => {
    patchGen({ attachments: (gen.attachments || []).filter((a) => a.url !== attachment.url) });
  };

  const versions = videoVersions(node);
  const activeIdx = versions.findIndex((v) => v.clipUrl === node.clipUrl);
  const downloadClip = () => {
    if (!node.videoUrl) return;
    const version = versions.find((v) => v.clipUrl === node.videoUrl) || versions[activeIdx];
    void downloadMedia(node.videoUrl, friendlyFileName({
      project: props.projectTitle,
      reference: node.title || 'video',
      url: node.videoUrl,
      at: version?.createdAt || Date.now(),
    }));
  };
  const status = busy
    ? (node.progress || (node.status === 'joining' ? 'Juntando…' : 'Gerando…'))
    : node.status === 'failed'
      ? 'Falhou'
      : node.videoUrl
        ? 'Pronto'
        : 'Sem status';
  const thumbs: Array<{ key: string; url: string; badge?: string; onClear?: () => void }> = [];
  if (gen.startImage?.url) thumbs.push({ key: 'start', url: gen.startImage.url, badge: 'Início', onClear: () => clearRef('startImage') });
  // Attachments keep model chronology (Stage 1…N). Badge the index when labeled.
  (gen.attachments || []).forEach((a, i) => {
    if (!a.url) return;
    const stageMatch = /^Stage\s+(\d+)$/i.exec(String(a.label || '').trim());
    thumbs.push({
      key: `a-${i}-${a.url}`,
      url: a.url,
      badge: stageMatch ? `S${stageMatch[1]}` : undefined,
      onClear: () => dropRef(a),
    });
  });
  if (gen.endImage?.url) thumbs.push({ key: 'end', url: gen.endImage.url, badge: 'Fim', onClear: () => clearRef('endImage') });
  if (!thumbs.length && meta?.refs) {
    meta.refs.slice(0, REFS_SHOWN).forEach((r) => {
      if (r.url) thumbs.push({ key: r.id, url: r.url });
    });
  }
  const previewUrls = generatorPreviewUrls(
    [gen.startImage?.url, ...(gen.attachments || []).map((a) => a.url), gen.endImage?.url],
    meta?.refs?.map((r) => r.url),
  );
  const showFrames = !node.videoUrl && previewUrls.length > 0;

  const rail: Array<{ slot: GeneratorSlot; label: string; icon: typeof ImageIcon; filled: boolean }> = [
    { slot: 'start', label: 'Imagem inicial', icon: ImageIcon, filled: Boolean(gen.startImage?.url) },
    { slot: 'end', label: 'Imagem final', icon: ImagePlus, filled: Boolean(gen.endImage?.url) },
    { slot: 'attachment', label: 'Anexos', icon: Images, filled: Boolean(gen.attachments?.length) },
    { slot: 'video', label: 'Vídeo de referência', icon: Film, filled: Boolean(gen.referenceVideo?.url) },
    { slot: 'audio', label: 'Áudio', icon: AudioLines, filled: Boolean(gen.audio?.url) },
  ];

  return (
    <div
      data-node-id={node.id}
      className={`tb-node tb-node--video tb-node--gen${selected ? ' is-selected' : ''}${linking ? ' is-link-target' : ''}${(receiving || busy || assembling) ? ' is-receive' : ''}${(busy || assembling) ? ' is-generating' : ''}`}
      style={nodeBoxStyle(node, props.aspect || '16:9')}
      onPointerDown={onPointerDown}
      onDragOver={(e) => { if (!locked) { e.preventDefault(); e.stopPropagation(); } }}
      onDrop={(e) => {
        if (locked) return;
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files || []);
        if (!files.length) return;
        const video = files.find((f) => f.type.startsWith('video/'));
        const audio = files.find((f) => f.type.startsWith('audio/'));
        const images = files.filter((f) => f.type.startsWith('image/'));
        if (video) { slotRef.current = 'video'; onFiles([video]); }
        else if (audio && !images.length) { slotRef.current = 'audio'; onFiles([audio]); }
        else if (images.length) { slotRef.current = 'attachment'; onFiles(images); }
      }}
    >
      <ReceiveRing on={Boolean(receiving || busy || assembling)} loop={Boolean(busy || assembling)} />
      <div className="tb-gen-head">
        <span className="tb-gen-title"><Sparkles size={13} /> <span>{node.title || 'Gerador de vídeos'}</span></span>
        {versions.length > 1 && (
          <span className="tb-video-versions" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Versão anterior" disabled={locked || activeIdx <= 0} onClick={() => props.onSelectVersion(versions[activeIdx - 1].id)}>
              <ChevronLeft size={13} />
            </button>
            <span>{activeIdx >= 0 ? `v${activeIdx + 1}` : '—'}/{versions.length}</span>
            <button type="button" aria-label="Próxima versão" disabled={locked || activeIdx < 0 || activeIdx >= versions.length - 1} onClick={() => props.onSelectVersion(versions[activeIdx + 1].id)}>
              <ChevronRight size={13} />
            </button>
          </span>
        )}
      </div>
      <div className="tb-gen-stage">
        {node.videoUrl && !busy && <video src={node.videoUrl} playsInline preload="metadata" />}
        {showFrames && <FrameCrossfade urls={previewUrls} />}
        {!node.videoUrl && !busy && (!showFrames || node.status === 'failed') && (
          <div className="tb-node-placeholder">
            {node.status === 'failed' ? <AlertTriangle size={18} /> : <Film size={22} />}
            <span>{node.status === 'failed' ? node.error || 'Falhou' : 'O vídeo aparece aqui'}</span>
          </div>
        )}
        {(busy || assembling) && (
          <div className={`tb-node-loading${showFrames ? ' is-over-frames' : ''}`}>
            <PixelAssemble label={busy ? (node.progress || (node.status === 'joining' ? 'Juntando…' : 'Gerando…')) : 'Preparando…'} />
          </div>
        )}
        <div className="tb-gen-top">
          <span className="tb-gen-chip">{clock(0)} – {clock(duration)}</span>
          <span className={`tb-gen-chip${node.status === 'failed' ? ' is-bad' : ''}`}>{status}</span>
          {settled && (
            <button
              type="button"
              className="tb-gen-download"
              aria-label="Baixar vídeo"
              title="Baixar vídeo"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                downloadClip();
              }}
            >
              <Download size={12} /> Baixar
            </button>
          )}
        </div>
      </div>
      {props.onOpen && (
        <button
          type="button"
          className="tb-gen-expand"
          aria-label="Expandir"
          title="Expandir"
          style={{ transform: `translate(-50%, -50%) scale(${1 / Math.max(0.2, (props.scale ?? 1) * (props.zoom ?? 1))})` }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            props.onOpen?.(e);
          }}
        >
          <Maximize2 size={18} />
        </button>
      )}
        {!busy && !settled && (
        <div
        className="tb-gen-panel"
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).closest('button, textarea, input, select, a')) e.stopPropagation();
        }}
        onWheel={(e) => e.stopPropagation()}
      >
          {props.onSetImageMode && (
            <VideoInputModeButtons
              className="tb-input-mode tb-input-mode--in-panel"
              mode={node.imageMode || 'keyframes'}
              onMode={props.onSetImageMode}
              disabled={locked || hidePrompt}
            />
          )}
          <div className="tb-gen-main">
          {thumbs.length > 0 && (
            <div className="tb-gen-thumbs">
              {thumbs.slice(0, REFS_SHOWN).map((t) => (
                <span key={t.key} className="tb-gen-thumb" title={t.badge}>
                  <img src={t.url} alt="" draggable={false} />
                  {t.badge && <em>{t.badge}</em>}
                  {t.onClear && !locked && (
                    <button type="button" aria-label={`Remover ${t.badge || 'anexo'}`} onClick={t.onClear}><X size={9} /></button>
                  )}
                </span>
              ))}
            </div>
          )}
          {(gen.referenceVideo?.url || gen.audio?.url) && (
            <div className="tb-gen-files">
              {gen.referenceVideo?.url && (
                <span title={gen.referenceVideo.label || 'Vídeo de referência'}>
                  <Film size={11} /> {gen.referenceVideo.label || 'Vídeo'}
                  {!locked && <button type="button" aria-label="Remover vídeo de referência" onClick={() => clearRef('referenceVideo')}><X size={10} /></button>}
                </span>
              )}
              {gen.audio?.url && (
                <span title={gen.audio.label || 'Áudio'}>
                  <AudioLines size={11} /> {gen.audio.label || 'Áudio'}
                  {!locked && <button type="button" aria-label="Remover áudio" onClick={() => clearRef('audio')}><X size={10} /></button>}
                </span>
              )}
            </div>
          )}
          {!hidePrompt && (
            <textarea
              value={prompt}
              readOnly={locked}
              rows={showsScript ? 4 : 2}
              placeholder="Descreva o vídeo…"
              aria-label="Prompt"
              title={showsScript ? 'Prompt final do roteiro. Edite e gere para o agente refazer o roteiro a partir do seu texto.' : 'Descreva o vídeo: ao gerar, o agente monta o roteiro a partir deste texto'}
              onChange={(e) => setPrompt(e.target.value)}
              onBlur={savePrompt}
            />
          )}
          </div>
          <div className="tb-gen-controls-stack">
            {locks.model ? (
              <span className="tb-gen-lock" title={lockTip}>
                <GenSelect
                  label="Modelo"
                  title={lockTip}
                  wide
                  disabled
                  value={modelId}
                  options={GEN_MODELS.map((m) => ({ id: m.id, label: m.label }))}
                  onChange={() => undefined}
                />
              </span>
            ) : (
              <GenSelect
                label="Modelo"
                title="Modelo de geração"
                wide
                disabled={locked}
                value={modelId}
                options={GEN_MODELS.map((m) => ({ id: m.id, label: m.label }))}
                onChange={setModel}
              />
            )}
            {locks.resolution ? (
              <span className="tb-gen-lock" title={lockTip}>
                <GenSelect
                  label="Resolução"
                  title={lockTip}
                  disabled
                  value={resolutionId}
                  options={(model?.resolutions || []).map((r) => ({ id: r.id, label: r.label }))}
                  onChange={() => undefined}
                />
              </span>
            ) : (
              <GenSelect
                label="Resolução"
                disabled={locked || !model}
                value={resolutionId}
                options={(model?.resolutions || []).map((r) => ({ id: r.id, label: r.label }))}
                onChange={(id) => props.onPatch?.({ resolutionId: id })}
              />
            )}
            {scriptSeconds || locks.duration ? (
              <span className="tb-gen-lock" title={locks.duration ? lockTip : durationTitle}>
                <GenSelect
                  label="Duração"
                  title={locks.duration ? lockTip : durationTitle}
                  disabled
                  value={String(scriptSeconds || duration)}
                  options={[{ id: String(scriptSeconds || duration), label: `${scriptSeconds || duration}s` }]}
                  onChange={() => undefined}
                />
              </span>
            ) : (
              <GenSelect
                label="Duração"
                disabled={locked || !model}
                value={String(duration)}
                options={model
                  ? Array.from({ length: (model.durationMax || duration) - (model.durationMin || 1) + 1 }, (_, i) => (model.durationMin || 1) + i)
                    .map((s) => ({ id: String(s), label: `${s}s` }))
                  : []}
                onChange={(id) => props.onPatch?.({ totalDurationSeconds: Number(id) })}
              />
            )}
            {props.aspect && (locks.aspect ? (
              <span className="tb-gen-lock" title={lockTip}>
                <GenSelect
                  label="Proporção"
                  title={lockTip}
                  disabled
                  value={props.aspect}
                  options={BOARD_ASPECTS.map((a) => ({ id: a, label: a }))}
                  onChange={() => undefined}
                />
              </span>
            ) : (
              <GenSelect
                label="Proporção"
                disabled={locked || !props.onAspect}
                value={props.aspect}
                options={BOARD_ASPECTS.map((a) => ({ id: a, label: a }))}
                onChange={(id) => props.onAspect?.(id as BoardAspect)}
              />
            ))}
            <button
              type="button"
              className={`tb-gen-switch${soundOn ? ' is-on' : ''}`}
              aria-pressed={soundOn}
              disabled={locked || !audioChoice || Boolean(locks.audio)}
              title={locks.audio ? lockTip : model && alwaysGeneratedAudio(model) ? 'Este modelo sempre gera com som' : 'Som nativo'}
              onClick={() => props.onPatch?.({ generateAudio: !soundOn })}
            >
              Som
            </button>
            {!hidePrompt && (
              <button
                type="button"
                className={`tb-gen-switch${gen.music ? ' is-on' : ''}`}
                aria-pressed={Boolean(gen.music)}
                disabled={locked}
                title="Pedir música de fundo no prompt"
                onClick={() => patchGen({ music: !gen.music })}
              >
                Música
              </button>
            )}
            {!hidePrompt && (
              <button
                type="button"
                className={`tb-gen-switch${gen.draft ? ' is-on' : ''}`}
                aria-pressed={Boolean(gen.draft)}
                disabled={locked}
                title="Rascunho: movimento mais simples"
                onClick={() => patchGen({ draft: !gen.draft })}
              >
                Rascunho
              </button>
            )}
          </div>
        </div>
        )}
        {!busy && !hidePrompt && (
          <button
            type="button"
            className="tb-gen-go"
            aria-label="Gerar vídeo"
            title={
              canGenerate
                ? 'Gerar vídeo'
                : hasRefVideo && !v2vOk
                  ? 'Escolha um modelo que aceita vídeo de referência'
                  : 'Adicione imagem inicial, final, anexos ou um vídeo de referência com prompt'
            }
            disabled={!canGenerate || uploading}
            onClick={(e) => {
              e.stopPropagation();
              props.onPatch?.({
                videoModelId: modelId,
                resolutionId,
                totalDurationSeconds: duration,
                generateAudio: soundOn,
                ...promptPatch(),
              });
              props.onGenerate?.();
            }}
          >
            {uploading ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
          </button>
        )}
      <div className="tb-gen-rail" onPointerDown={(e) => e.stopPropagation()}>
        {rail.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.slot}
              type="button"
              className={item.filled ? 'is-on' : ''}
              title={item.label}
              aria-label={item.label}
              disabled={locked}
              onClick={() => openSlot(item.slot)}
            >
              <Icon size={15} />
            </button>
          );
        })}
      </div>
      <input
        ref={fileRef}
        type="file"
        hidden
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <NodeChrome
        {...props}
        inputMode={props.onSetImageMode ? (node.imageMode || 'keyframes') : undefined}
      />
    </div>
  );
}

function VideoNodeView(props: NodeViewProps & {
  node: BoardVideoNode;
  meta?: VideoMeta;
  projectTitle?: string;
  onSelectVersion: (versionId: string) => void;
  onSetImageMode?: (mode: BoardImageMode) => void;
  onPatch?: (patch: Partial<BoardVideoNode>) => void;
  onUpload?: (slot: GeneratorSlot, file: File) => void;
  onGenerate?: () => void;
  aspect?: BoardAspect;
  onAspect?: (aspect: BoardAspect) => void;
  assembling?: boolean;
}) {
  if (!props.node.edit && !props.node.fromGallery) return <GeneratorNodeView {...props} />;
  const { node, meta, selected, linking, receiving, readOnly, onPointerDown, assembling } = props;
  const busy = node.status === 'generating' || node.status === 'joining';
  const locked = busy || !!readOnly;
  const versions = videoVersions(node);
  const activeIdx = versions.findIndex((v) => v.clipUrl === node.clipUrl);
  const mode = node.imageMode || 'keyframes';
  const showMode = Boolean(props.onSetImageMode) && !node.edit && !node.fromGallery && (meta?.scenes || 0) >= 2;
  const download = () => {
    if (!node.videoUrl) return;
    const version = versions.find((v) => v.clipUrl === node.videoUrl) || versions[activeIdx];
    const reference = node.title || (node.edit ? 'editado' : meta?.label) || 'video';
    void downloadMedia(node.videoUrl, friendlyFileName({
      project: props.projectTitle,
      reference,
      url: node.videoUrl,
      at: version?.createdAt || Date.now(),
    }));
  };
  const stepVersion = (delta: number) => {
    const next = versions[activeIdx + delta];
    if (next) props.onSelectVersion(next.id);
  };
  const info = [
    node.fromGallery ? 'Da galeria' : null,
    meta?.scenes ? `${meta.scenes} cenas` : null,
    meta?.seconds ? `${meta.seconds}s` : null,
    meta?.joins ? `+${meta.joins} junção` : null,
    node.edit ? `${meta?.tracks || 0} ${meta?.tracks === 1 ? 'faixa' : 'faixas'} de som` : null,
  ].filter(Boolean).join(' · ');
  const fades = [
    !node.edit && node.joinTransition
      ? { label: `${joinTransitionLabel(node.joinTransition)} entre vídeos`, title: 'Aplicado em cada emenda da junção' }
      : null,
    node.edit?.fade ? { label: 'Fade entrada/saída', title: 'Fade do preto no começo e para o preto no fim' } : null,
    node.edit?.grayscale ? { label: 'Preto e branco', title: 'Vídeo dessaturado' } : null,
    node.edit?.grade && !node.edit?.grayscale ? { label: 'Cor cinematográfica', title: 'Curva de cor cinematográfica' } : null,
  ].filter((f): f is { label: string; title: string } => Boolean(f));
  const TitleIcon = node.edit ? SlidersHorizontal : Film;
  const asGenChrome = Boolean(node.edit);
  return (
    <div
      data-node-id={node.id}
      className={`tb-node tb-node--video${node.edit ? ' tb-node--edit tb-node--gen' : ''}${selected ? ' is-selected' : ''}${linking ? ' is-link-target' : ''}${(receiving || busy) ? ' is-receive' : ''}${busy ? ' is-generating' : ''}`}
      style={nodeBoxStyle(node, props.aspect || '16:9')}
      onPointerDown={onPointerDown}
      onClick={props.onOpen}
    >
      <ReceiveRing on={receiving || busy} loop={busy} />
      {asGenChrome && (
        <div className="tb-gen-head">
          <span className="tb-gen-title"><TitleIcon size={13} /> <span>{node.title || 'Vídeo editado'}</span></span>
          {versions.length > 1 && (
            <span className="tb-video-versions" onPointerDown={(e) => e.stopPropagation()}>
              <button type="button" aria-label="Versão anterior" disabled={locked || activeIdx <= 0} onClick={() => stepVersion(-1)}>
                <ChevronLeft size={13} />
              </button>
              <span>{activeIdx >= 0 ? `v${activeIdx + 1}` : '—'}/{versions.length}</span>
              <button type="button" aria-label="Próxima versão" disabled={locked || activeIdx < 0 || activeIdx >= versions.length - 1} onClick={() => stepVersion(1)}>
                <ChevronRight size={13} />
              </button>
            </span>
          )}
        </div>
      )}
      <div className={asGenChrome ? 'tb-gen-stage' : 'tb-node-media tb-node-media--video'}>
        {node.videoUrl && !busy && <video src={node.videoUrl} playsInline preload="metadata" />}
        {showMode && (
          <span className="tb-mode-toggle" role="radiogroup" aria-label="Como as imagens guiam o vídeo" onPointerDown={(e) => e.stopPropagation()}>
            {([
              ['keyframes', 'Quadros-chave', 'Um vídeo só, passando pelas imagens na ordem das setas. Com 2 imagens começa e termina exatamente nelas'],
              ['reference', 'Referência', 'Cada imagem liga direto no vídeo e guia o visual, sem ordem no tempo'],
            ] as const).map(([id, label, title]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={mode === id}
                className={mode === id ? 'is-on' : ''}
                title={title}
                disabled={locked}
                onClick={() => props.onSetImageMode?.(id)}
              >
                {label}
              </button>
            ))}
          </span>
        )}
        {fades.length > 0 && (
          <span className="tb-node-fx">
            {fades.map((f) => (
              <span key={f.label} className="tb-node-fx-chip" title={f.title}>
                <Blend size={10} /> {f.label}
              </span>
            ))}
          </span>
        )}
        {asGenChrome && (
          <div className="tb-gen-top">
            {info ? <span className="tb-gen-chip">{info}</span> : <span className="tb-gen-chip">Edição</span>}
            <span className={`tb-gen-chip${node.status === 'failed' ? ' is-bad' : ''}`}>
              {busy ? (node.progress || 'Mixando…') : node.status === 'failed' ? 'Falhou' : node.videoUrl ? 'Pronto' : 'Aguardando'}
            </span>
            {node.videoUrl && !busy && (
              <button type="button" className="tb-gen-download" aria-label="Baixar vídeo" title="Baixar vídeo" onPointerDown={(e) => e.stopPropagation()} onClick={download}>
                <Download size={13} />
              </button>
            )}
          </div>
        )}
        {(busy || assembling) && (
          <div className="tb-node-loading">
            <PixelAssemble label={busy ? (node.progress || (node.status === 'joining' ? 'Juntando vídeos…' : 'Gerando…')) : 'Preparando…'} />
          </div>
        )}
        {!busy && !node.videoUrl && (
          <div className="tb-node-placeholder">
            {node.status === 'failed' ? <AlertTriangle size={18} /> : <Film size={20} />}
            <span>
              {node.status === 'failed' ? node.error || 'Falhou' : node.edit ? 'Aguardando o vídeo de origem' : 'Aguardando geração'}
            </span>
            {node.status !== 'failed' && !readOnly && <small>{node.edit ? 'ligue um vídeo pronto aqui' : 'peça “gerar vídeo” no chat'}</small>}
          </div>
        )}
      </div>
      {!asGenChrome && (
        <div className="tb-video-foot">
          <span className="tb-video-title" title={node.title}>
            <TitleIcon size={14} /> <span>{node.title || 'Vídeo'}</span>
          </span>
          {info && <span className="tb-video-info">{info}</span>}
          {node.videoUrl && !busy && (
            <button
              type="button"
              className="tb-video-download"
              aria-label="Baixar vídeo"
              title="Baixar vídeo"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={download}
            >
              <Download size={13} />
            </button>
          )}
          {versions.length > 1 && (
            <span className="tb-video-versions" onPointerDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Versão anterior"
                title="Versão anterior"
                disabled={locked || activeIdx <= 0}
                onClick={() => stepVersion(-1)}
              >
                <ChevronLeft size={13} />
              </button>
              <span title="Versão ativa deste fluxo">
                {activeIdx >= 0 ? `v${activeIdx + 1}` : '—'}/{versions.length}
              </span>
              <button
                type="button"
                aria-label="Próxima versão"
                title="Próxima versão"
                disabled={locked || activeIdx < 0 || activeIdx >= versions.length - 1}
                onClick={() => stepVersion(1)}
              >
                <ChevronRight size={13} />
              </button>
            </span>
          )}
        </div>
      )}
      {!node.edit && !node.fromGallery && meta?.refs && meta.refs.length > 0 && (
        <div className="tb-video-refs" title={`${meta.refs.length} ${meta.refs.length === 1 ? 'imagem ligada' : 'imagens ligadas'} a este vídeo`}>
          {meta.refs.slice(0, REFS_SHOWN).map((r, i) => (
            <span key={r.id} className={`tb-video-ref${r.url ? '' : ' is-pending'}`} title={`${i + 1}. ${r.label}`}>
              {r.url ? <img src={r.url} alt="" draggable={false} /> : <Sparkles size={9} />}
            </span>
          ))}
          {meta.refs.length > REFS_SHOWN && <span className="tb-video-ref is-more">+{meta.refs.length - REFS_SHOWN}</span>}
        </div>
      )}
      <NodeChrome {...props} />
    </div>
  );
}
